import {
  ActionIcon,
  Button,
  Divider,
  Flex,
  Group,
  Menu,
  Stack,
  Switch,
  Text,
  Tooltip,
} from "@mantine/core"
import { useHover } from "@mantine/hooks"
import { IconFileTypeCsv, IconColumns } from "@tabler/icons-react"
import { generateCsv, download, mkConfig } from "export-to-csv"
import {
  createMRTColumnHelper,
  useMantineReactTable,
  MantineReactTable,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleDensePaddingButton,
  MRT_TableInstance,
  MRT_RowData,
} from "mantine-react-table"
import { useMemo } from "react"

import { ExploreResponses } from "@costory/types/endpoints/explorer"
import { Currency } from "@costory/types/prisma-client"

import { formatNumber } from "@costory/shared/utils/format"

import { TrendBadge } from "@costory/front/components/TrendBadge"
import { CustomCopyCellButton } from "@costory/front/components/common/CustomCopyCellButtton"
import { DrillDownCell } from "@costory/front/components/table/DrillDownCell"
import { NumberCell } from "@costory/front/components/table/NumberCell"
import { TruncatedHeader } from "@costory/front/components/table/TruncatedHeader"
import { useChartContext } from "@costory/front/contexts/ChartContext"
import { truncateLabel } from "@costory/front/utils/charts"
import {
  CURRENCY_FILTER_VARIANT,
  getCommonTableStyleProps,
} from "@costory/front/utils/table"

type Props = {
  data: ExploreResponses.Waterfall["dataGroupedByValue"]
  drillDownInto: (value: string, mode: "sameWindow" | "newTab") => void
  currency: Currency
  isDataTable: boolean
  setIsDataTable: (checked: boolean) => void
}
type TopToolbarCustomActionsProps = {
  isDataTable: boolean
  onToggleTableView: (checked: boolean) => void
}
type ToolbarInternalActionsProps<
  TData extends
    | ExploreResponses.ExplorerData["dataTable"][0]
    | ExploreResponses.Waterfall["dataGroupedByValue"][0],
> = {
  table: MRT_TableInstance<TData>
  data: TData[]
}

export const TopToolbarCustomActions = ({
  isDataTable,
  onToggleTableView,
}: TopToolbarCustomActionsProps) => {
  const { currentPeriod, previousPeriod } = useChartContext()
  return (
    <Flex align="center" gap="lg" wrap="nowrap" mt={10}>
      <Group gap="xs">
        <Text fw={700}>Selected Period:</Text>
        <Text>{currentPeriod}</Text>
      </Group>
      <Group gap="xs">
        <Text fw={700}>Reference Period:</Text>
        <Text>{previousPeriod}</Text>
      </Group>
      <Group gap="xs">
        <Text>DataTable</Text>
        <Switch
          size="md"
          checked={isDataTable}
          onChange={(event) => onToggleTableView(event.currentTarget.checked)}
        />
      </Group>
    </Flex>
  )
}
// Custom show/hide columns menu to reduced size.
interface CustomShowHideColumnsButtonProps<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>
}
const CustomShowHideColumnsButton = <TData extends MRT_RowData>({
  table,
}: CustomShowHideColumnsButtonProps<TData>) => {
  const columns = table.getAllLeafColumns()

  const toggleColumnVisibility = (columnId: string) => {
    const column = table.getColumn(columnId)
    column?.toggleVisibility()
  }

  const handleToggleAll = (visible: boolean) => {
    columns.forEach((col) => {
      const column = table.getColumn(col.id)
      const isVisible = column?.getIsVisible()
      if (isVisible !== visible) {
        column?.toggleVisibility()
      }
    })
  }
  const allVisible = columns.every((col) =>
    table.getColumn(col.id)?.getIsVisible(),
  )
  const allHidden = columns.every(
    (col) => !table.getColumn(col.id)?.getIsVisible(),
  )
  const { hovered, ref } = useHover()
  return (
    <Menu width="auto" shadow="md">
      <Menu.Target>
        <Tooltip label="Show/Hide column">
          <ActionIcon
            variant="transparent"
            c="textSecondary"
            ref={ref}
            size="lg"
            bg={hovered ? "surfaceHover" : "transparent"}
          >
            <IconColumns size="inherit" />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown style={{ maxHeight: "300px", overflow: "auto" }}>
        <Group justify="space-between" px="sm" py={4}>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => handleToggleAll(false)}
            disabled={allHidden}
          >
            Hide all
          </Button>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => handleToggleAll(true)}
            disabled={allVisible}
          >
            Show all
          </Button>
        </Group>

        <Divider my="xs" />

        <Stack px="sm">
          {columns.map((col) => (
            <Switch
              key={col.id}
              label={col.columnDef.header as string}
              checked={col.getIsVisible()}
              onChange={() => toggleColumnVisibility(col.id)}
            />
          ))}
        </Stack>
      </Menu.Dropdown>
    </Menu>
  )
}

export const ToolbarInternalActions = <
  TData extends
    | ExploreResponses.ExplorerData["dataTable"][0]
    | ExploreResponses.Waterfall["dataGroupedByValue"][0],
>({
  table,
  data,
}: ToolbarInternalActionsProps<TData>) => {
  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
  })
  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(data)
    download(csvConfig)(csv)
  }
  return (
    <>
      <Tooltip label="Export to CSV">
        <Button
          onClick={handleExportData}
          aria-label="Export to CSV"
          variant="transparent"
        >
          <IconFileTypeCsv />
        </Button>
      </Tooltip>

      <MRT_ToggleGlobalFilterButton table={table} />
      <MRT_ToggleFiltersButton table={table} />
      <CustomShowHideColumnsButton table={table} />
      <MRT_ToggleDensePaddingButton table={table} />
      <MRT_ToggleFullScreenButton table={table} />
    </>
  )
}

export const TableChart = ({
  data,
  drillDownInto,
  currency,
  isDataTable,
  setIsDataTable,
}: Props) => {
  const { filterForm, groupByOptions } = useChartContext()
  const { groupBy } = filterForm.getValues()
  const valueLabel =
    groupByOptions
      .map((g) => g.items)
      .flat()
      .find((i) => i.value === groupBy)?.label || "Value"

  const changes = data
    .map((item) => item.change)
    .filter((value): value is number => typeof value === "number")

  const isPositiveDominant =
    Math.abs(Math.max(...changes)) >= Math.abs(Math.min(...changes))

  const columns = useMemo(() => {
    const calculateRange = (
      key: keyof ExploreResponses.Waterfall["dataGroupedByValue"][0],
    ) => {
      const values = data

        .map((item) => item[key])
        .filter((value): value is number => typeof value === "number")

      return {
        min: Math.min(...values),
        max: Math.max(...values),
      }
    }
    const columnHelper =
      createMRTColumnHelper<
        ExploreResponses.Waterfall["dataGroupedByValue"][0]
      >()

    const changeRange = calculateRange("change")
    const relativeChangeRange = calculateRange("relativeChange")
    const periodCost = calculateRange("periodCost")
    const previousPeriodCost = calculateRange("previousPeriodCost")
    return [
      columnHelper.accessor("groupByValue", {
        header: truncateLabel(valueLabel, 20),
        Header: () => <TruncatedHeader label={valueLabel} />,
        filterVariant: "autocomplete",
        size: Math.min(300, Math.max(valueLabel.length * 12, 200)),
        Cell: ({ cell }) => (
          <DrillDownCell
            value={cell.getValue<string>()}
            drillDownInto={drillDownInto}
          />
        ),
      }),
      columnHelper.accessor("periodCost", {
        header: "Selected Period",
        ...CURRENCY_FILTER_VARIANT,
        mantineFilterRangeSliderProps: {
          min: periodCost.min - 1,
          max: periodCost.max + 2,
          label: (value) => formatNumber({ value }),
        },
        Cell: ({ cell }) => (
          <CustomCopyCellButton cell={cell}>
            <NumberCell value={cell.getValue()} currency={currency} />
          </CustomCopyCellButton>
        ),
      }),
      columnHelper.accessor("previousPeriodCost", {
        header: "Reference Period",
        ...CURRENCY_FILTER_VARIANT,
        mantineFilterRangeSliderProps: {
          min: previousPeriodCost.min - 1,
          max: previousPeriodCost.max + 2,
          label: (value) => formatNumber({ value }),
        },
        Cell: ({ cell }) => (
          <CustomCopyCellButton cell={cell}>
            <NumberCell value={cell.getValue()} currency={currency} />
          </CustomCopyCellButton>
        ),
      }),
      columnHelper.accessor("change", {
        header: "Change (Absolute)",
        ...CURRENCY_FILTER_VARIANT,
        mantineFilterRangeSliderProps: {
          min: changeRange.min - 1,
          max: changeRange.max + 2,
          label: (value) => formatNumber({ value }),
        },
        Cell: ({ cell }) => (
          <CustomCopyCellButton cell={cell}>
            <NumberCell value={cell.getValue()} currency={currency} />
          </CustomCopyCellButton>
        ),
      }),
      columnHelper.accessor("relativeChange", {
        header: "Relative change (%)",
        ...CURRENCY_FILTER_VARIANT,
        mantineFilterRangeSliderProps: {
          min: relativeChangeRange.min * 100 - 1,
          max: relativeChangeRange.max * 100 + 2,
          label: (value) => {
            return formatNumber({ value: value / 100, numberType: "percent" })
          },
          step: 0.1,
        },
        filterFn: (row, columnId, filterValue) => {
          const value: number = row.getValue(columnId)
          // Return true if the value falls within the range specified by filterValue
          return value >= filterValue[0] / 100 && value <= filterValue[1] / 100
        },
        Cell: ({ cell, row }) => (
          <Button
            variant="transparent"
            onClick={(event) => {
              const mode = event.metaKey ? "newTab" : "sameWindow"

              drillDownInto(cell.row.original.groupByValue, mode)
            }}
          >
            <TrendBadge
              current={row.original.periodCost}
              previous={row.original.previousPeriodCost}
            />
          </Button>
        ),
      }),
    ]
  }, [drillDownInto, currency, data, valueLabel])

  const table = useMantineReactTable<
    ExploreResponses.Waterfall["dataGroupedByValue"][0]
  >({
    columns,
    data,
    initialState: {
      sorting: [{ id: "change", desc: isPositiveDominant }],
    },
    renderToolbarInternalActions: ({ table }) => (
      <ToolbarInternalActions table={table} data={data} />
    ),
    renderTopToolbarCustomActions: () => (
      <TopToolbarCustomActions
        isDataTable={isDataTable}
        onToggleTableView={(checked) => setIsDataTable(checked)}
      />
    ),
    ...getCommonTableStyleProps(),
  })
  return <MantineReactTable table={table} />
}
